// src/layout/Header.js
import React, { useState } from 'react';
import Logo from '../common/Logo';

const Header = ({ showPage, cartItemCount, isAuthenticated, userRole, userName }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleNavLinkClick = (page) => {
        showPage(page);
        setIsMobileMenuOpen(false); // Close mobile menu after clicking a link
    };

    return (
        <header className="bg-white/95 backdrop-blur-md sticky top-0 z-50 shadow-sm border-b border-gray-100">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-20">
                {/* Logo/Brand */}
                 <div className="cursor-pointer flex items-center gap-2 transform transition-transform hover:scale-105" onClick={() => handleNavLinkClick('landing')}>
                    <Logo />
                </div>


                {/* Mobile Icons (Top Right) */}
                <div className="md:hidden flex items-center space-x-3">
                    {(userRole === 'admin' || userRole === 'super_admin') && (
                        <button onClick={() => handleNavLinkClick('admin-orders')} className="text-violet-600 hover:text-violet-800 transition-colors focus:outline-none flex items-center" title="Admin Panel">
                            <i className="fas fa-user-shield text-xl"></i>
                        </button>
                    )}
                    {userRole === 'driver' && (
                        <button onClick={() => handleNavLinkClick('driver-dashboard')} className="text-orange-500 hover:text-orange-700 transition-colors focus:outline-none flex items-center" title="Driver Panel">
                            <i className="fas fa-motorcycle text-xl"></i>
                        </button>
                    )}
                    <button onClick={() => handleNavLinkClick('cart')} className="relative text-sky-600 hover:text-sky-800 transition-colors focus:outline-none" title="Cart">
                        <i className="fas fa-shopping-cart text-xl"></i>
                        {cartItemCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                {cartItemCount}
                            </span>
                        )}
                    </button>
                </div>

                {/* Desktop Navigation Links */}
                <nav className="hidden md:flex items-center space-x-8">
                    {(userRole === 'admin' || userRole === 'super_admin') && (
                        <button onClick={() => handleNavLinkClick('admin-orders')} className="text-violet-600 hover:text-violet-800 transition-colors font-bold"><i className="fas fa-user-shield mr-1"></i> Admin Panel</button>
                    )}
                    {userRole === 'driver' && (
                        <button onClick={() => handleNavLinkClick('driver-dashboard')} className="text-orange-600 hover:text-orange-800 transition-colors font-bold"><i className="fas fa-motorcycle mr-1"></i> Driver Panel</button>
                    )}
                    <button onClick={() => handleNavLinkClick('products')} className="text-slate-600 hover:text-indigo-600 transition-colors font-medium">Products</button>
                    {/* Services Dropdown */}
                    <div className="relative group">
                        <button className="text-slate-600 hover:text-sky-600 transition-colors font-medium flex items-center gap-1 py-2">
                            Services <i className="fas fa-chevron-down text-xs ml-1 transition-transform group-hover:rotate-180"></i>
                        </button>
                        <div className="absolute top-full left-0 w-48 bg-white border border-gray-100 shadow-xl rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top z-50">
                            <button onClick={() => handleNavLinkClick('upload-prescription')} className="w-full text-left px-4 py-3 hover:bg-sky-50 text-slate-700 hover:text-sky-700 transition-colors flex items-center gap-3 border-b border-gray-50 font-medium text-sm">
                                <i className="fas fa-file-prescription w-5 text-center text-sky-500"></i> Upload Rx
                            </button>
                            <button onClick={() => handleNavLinkClick('labs')} className="w-full text-left px-4 py-3 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 transition-colors flex items-center gap-3 border-b border-gray-50 font-medium text-sm">
                                <i className="fas fa-flask w-5 text-center text-emerald-500"></i> Lab Tests
                            </button>
                            <button onClick={() => handleNavLinkClick('health-insurance')} className="w-full text-left px-4 py-3 hover:bg-red-50 text-slate-700 hover:text-red-700 transition-colors flex items-center gap-3 font-medium text-sm">
                                <i className="fas fa-heartbeat w-5 text-center text-red-500"></i> Insurance
                            </button>
                        </div>
                    </div>
                    <button onClick={() => handleNavLinkClick('orders')} className="text-slate-600 hover:text-sky-600 transition-colors font-medium">Orders</button>
                    
                    {/* Divider */}
                    <div className="h-6 w-px bg-gray-200"></div>
                    
                    <button onClick={() => handleNavLinkClick('cart')} className="relative text-sky-600 hover:text-sky-800 transition-colors p-2">
                        <i className="fas fa-shopping-cart text-xl"></i>
                        {cartItemCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold ring-2 ring-white shadow-sm">
                                {cartItemCount}
                            </span>
                        )}
                    </button>
                    
                    {isAuthenticated ? (
                        <div className="flex items-center space-x-3">
                            <button onClick={() => handleNavLinkClick('profile')} className="flex items-center gap-2 bg-sky-50 text-sky-700 px-4 py-2 rounded-full hover:bg-sky-100 transition-colors font-bold text-sm">
                                <i className="fas fa-user-circle text-lg"></i>
                                {userName ? userName.split(' ')[0] : 'Profile'}
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => handleNavLinkClick('login')} className="bg-sky-600 text-white px-6 py-2.5 rounded-full hover:bg-sky-700 transition-all font-medium text-sm shadow-md shadow-sky-600/20">Sign In</button>
                    )}
                </nav>
            </div>

            {/* Mobile Menu (conditionally rendered) */}
            {/* The mobile menu now slides from the left */}
            <nav className={`fixed top-0 ${isMobileMenuOpen ? 'left-0' : '-left-full'} w-64 h-full bg-emerald-600 text-white p-4 shadow-lg z-50 transition-all duration-300 ease-in-out md:hidden`}>
                <ul className="flex flex-col space-y-1 pt-12"> {/* Reduced spacing and padding */}
                    {/* Close button for mobile menu */}
                    <li className="absolute top-4 right-4">
                        <button onClick={toggleMobileMenu} className="text-white text-2xl focus:outline-none">
                            <i className="fas fa-times"></i> {/* Font Awesome X icon */}
                        </button>
                    </li>

                        {(userRole === 'admin' || userRole === 'super_admin') && (
                            <li><button onClick={() => handleNavLinkClick('admin-orders')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors font-bold bg-emerald-700">Admin Panel</button></li>
                        )}
                        {userRole === 'driver' && (
                            <li><button onClick={() => handleNavLinkClick('driver-dashboard')} className="block w-full text-left py-2 px-3 hover:bg-amber-600 rounded-md transition-colors font-bold bg-amber-500 text-white"><i className="fas fa-motorcycle mr-2"></i> Driver Panel</button></li>
                        )}
                        <li><button onClick={() => handleNavLinkClick('products')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors">Products</button></li>
                        
                        <li className="py-1">
                            <div className="bg-emerald-700/30 rounded-lg py-2">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-200 block mb-1 px-4">Services</span>
                                <button onClick={() => handleNavLinkClick('upload-prescription')} className="block w-full text-left py-2 px-4 hover:bg-emerald-500 transition-colors text-sm"><i className="fas fa-file-prescription w-5 text-center mr-2 text-sky-300"></i> Upload Prescription</button>
                                <button onClick={() => handleNavLinkClick('labs')} className="block w-full text-left py-2 px-4 hover:bg-emerald-500 transition-colors text-sm"><i className="fas fa-flask w-5 text-center mr-2 text-sky-300"></i> Lab Tests</button>
                                <button onClick={() => handleNavLinkClick('health-insurance')} className="block w-full text-left py-2 px-4 hover:bg-emerald-500 transition-colors text-sm"><i className="fas fa-shield-heart w-5 text-center mr-2 text-sky-300"></i> Health Insurance</button>
                            </div>
                        </li>
                        <li><button onClick={() => handleNavLinkClick('orders')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors">My Orders</button></li>
                        <li><button onClick={() => handleNavLinkClick('track-order')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors">Track Order</button></li>
                        <li><button onClick={() => handleNavLinkClick('cart')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors">Cart ({cartItemCount})</button></li>
                        <li>
                            {isAuthenticated ? (
                                <button onClick={() => handleNavLinkClick('profile')} className="block w-full text-left py-2 px-3 hover:bg-emerald-500 rounded-md transition-colors font-bold"><i className="fas fa-user-circle mr-2"></i> My Profile</button>
                            ) : (
                                <button onClick={() => handleNavLinkClick('login')} className="block w-full text-left py-2 px-3 bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors">Login/Register</button>
                            )}
                        </li>
                    </ul>
                </nav>
          
            {/* Overlay for when mobile menu is open */}
            {isMobileMenuOpen && (
                <div onClick={toggleMobileMenu} className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"></div>
            )}
        </header>
    );
};

export default Header;